import { Stack } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { _updateContactCategories } from '@/api/address-book/mutations';
import { _getCategories } from '@/api/address-book/queries';
import type { Contact } from '@/api/address-book/types';
import { ContactView } from '@/api/address-book-bff/types';
import type { TFetchError } from '@/api/api-error.types';
import EditIcon from '@/assets/svg/ui-elements/edit-outline.svg';
import { OPEN_CATEGORY_REQUIRED_REASON } from '@/features/address-book/locked-category';
import { CategoryAccordion } from '@/pages/address-book/modules/category-accordion';
import { selectsOpenCategory, toggleCategoryNode } from '@/pages/address-book/utils';
import { PagePaths } from '@/routes/page-paths';
import { Alert, Drawer, EmptyState, Spinner, useToast } from '@/shared/design-system';
import { useAddressBookCategoryScope } from '@/shared/modules/permissions';
import { getApiErrorMessage } from '@/utils/api-error-message';

import { PickerBody } from './styled';

interface Props {
  open: boolean;
  onOpenChange: (_value: boolean) => void;
  contact: Contact | ContactView;
  initialSelectedIds?: string[];
}

// Router state shape used to reopen this drawer (with its previous selection) after the user
// navigates away to Category Management via `handleManageCategories` and comes back.
export interface AssignCategoriesReturnState {
  openAssignCategoriesFor?: string;
  selectedIds?: string[];
}

export const AssignCategoriesModal: FC<Props> = ({
  open,
  onOpenChange,
  contact,
  initialSelectedIds,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(initialSelectedIds ?? contact.categoryNodeIds),
  );
  const categoryNodeIdsRef = useRef(contact.categoryNodeIds);
  categoryNodeIdsRef.current = contact.categoryNodeIds;
  const initialSelectedIdsRef = useRef(initialSelectedIds);
  initialSelectedIdsRef.current = initialSelectedIds;

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(initialSelectedIdsRef.current ?? categoryNodeIdsRef.current));
    }
  }, [open]);

  const { data: categories = [], isLoading } = useQuery({ ..._getCategories(), enabled: open });
  const { isScoped } = useAddressBookCategoryScope();
  const { add: addToast } = useToast();

  const { mutate: updateContactCategories, isPending } = useMutation({
    mutationFn: _updateContactCategories,
    onSuccess: () => onOpenChange(false),
    onError: (error) =>
      addToast({
        title: 'Could not save categories',
        description: getApiErrorMessage(error as TFetchError, 'Please try again.'),
        data: { status: 'error' },
      }),
  });

  // The server refuses a save that leaves a caller without full view no open category on the
  // contact, because the contact would then be hidden from that caller.
  const keepsOpenCategory = !isScoped || selectsOpenCategory(categories, [...selectedIds]);

  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange]);

  const handleToggle = useCallback(
    (id: string) => {
      setSelectedIds((prev) => new Set(toggleCategoryNode([...prev], categories, id)));
    },
    [categories],
  );

  const onSave = useCallback(() => {
    updateContactCategories({
      id: contact.id,
      body: { categoryNodeIds: [...selectedIds] },
    });
  }, [updateContactCategories, contact.id, selectedIds]);

  const handleManageCategories = useCallback(() => {
    navigate(PagePaths.addressBookCategories, {
      state: {
        returnTo: `${location.pathname}${location.search}`,
        returnState: {
          openAssignCategoriesFor: contact.id,
          selectedIds: [...selectedIds],
        },
      },
    });
    onOpenChange(false);
  }, [navigate, location.pathname, location.search, contact.id, selectedIds, onOpenChange]);

  const hasCategories = categories.length > 0;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="Assign Categories"
      description={`Select categories for ${contact.name}`}
      maxWidth={504}
      leftAction={
        hasCategories
          ? {
              children: (
                <>
                  <EditIcon width={16} height={16} />
                  Edit categories
                </>
              ),
              onClick: handleManageCategories,
            }
          : undefined
      }
      secondaryAction={
        hasCategories ? { children: 'Cancel', color: 'tertiary', onClick: handleClose } : undefined
      }
      primaryAction={
        hasCategories
          ? { children: 'Save Changes', onClick: onSave, disabled: isPending || !keepsOpenCategory }
          : undefined
      }
    >
      {isLoading ? (
        <Stack padding="40px" alignItems="center" justifyContent="center">
          <Spinner />
        </Stack>
      ) : !hasCategories ? (
        <EmptyState
          variant="tertiary"
          title="You haven't created any categories."
          action={{ label: 'Create new categories', onClick: handleManageCategories }}
        />
      ) : (
        <PickerBody>
          {!keepsOpenCategory && (
            <Alert variant="info" size="md" description={OPEN_CATEGORY_REQUIRED_REASON} />
          )}
          {open && (
            <CategoryAccordion
              categories={categories}
              selectedIds={selectedIds}
              onToggle={handleToggle}
            />
          )}
        </PickerBody>
      )}
    </Drawer>
  );
};
